import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useInView, animate } from 'framer-motion';
import {
  Menu, X, ChevronRight, ArrowUpRight, ArrowDown, Eye, Phone, Mail, MapPin,
  MessageCircle, Send, CheckCircle2, Gem, Layers, Building2, Hammer, Ruler,
  ShieldCheck, Users, Award, Cpu, Compass, Boxes, Workflow, AtSign, Globe
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import Lenis from 'lenis';
import MarbleSlab3D from './components/MarbleSlab3D';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMG = {
  hero: "https://images.unsplash.com/photo-1745301558339-44eb3217d5da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtYXJibGUlMjBpbnRlcmlvciUyMGFyY2hpdGVjdHVyZSUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzg2NDUzOTcyfDA&ixlib=rb-4.1.0&q=85",
  about: "https://images.unsplash.com/photo-1757924461488-ef9ad0670978?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtYXJibGUlMjBpbnRlcmlvciUyMGFyY2hpdGVjdHVyZSUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzg2NDUzOTcyfDA&ixlib=rb-4.1.0&q=85",
  chandelier: "https://images.unsplash.com/photo-1648881806148-e5c51179c826?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtYXJibGUlMjBpbnRlcmlvciUyMGFyY2hpdGVjdHVyZSUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzg2NDUzOTcyfDA&ixlib=rb-4.1.0&q=85",
  window: "https://images.unsplash.com/photo-1637649228998-6c78a67dfa6c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtYXJibGUlMjBpbnRlcmlvciUyMGFyY2hpdGVjdHVyZSUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzg2NDUzOTcyfDA&ixlib=rb-4.1.0&q=85",
  slab1: "https://images.unsplash.com/photo-1715540257260-673b2cf56112?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxpdGFsaWFuJTIwd2hpdGUlMjBtYXJibGUlMjBzbGFiJTIwdGV4dHVyZSUyMGNsb3NldXB8ZW58MHx8fHwxNzg2NDUzOTcyfDA&ixlib=rb-4.1.0&q=85",
  slab2: "https://images.unsplash.com/photo-1533457460068-43faf66356f9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxpdGFsaWFuJTIwd2hpdGUlMjBtYXJibGUlMjBzbGFiJTIwdGV4dHVyZSUyMGNsb3NldXB8ZW58MHx8fHwxNzg2NDUzOTcyfDA&ixlib=rb-4.1.0&q=85",
  pentBed: "https://images.unsplash.com/photo-1702411200201-3061d0eea802?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBwZW50aG91c2UlMjBpbnRlcmlvciUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODY0NTM5ODR8MA&ixlib=rb-4.1.0&q=85",
  pentLiv: "https://images.unsplash.com/photo-1565623833408-d77e39b88af6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBwZW50aG91c2UlMjBpbnRlcmlvciUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODY0NTM5ODR8MA&ixlib=rb-4.1.0&q=85",
};

/* ============================== DATA ============================== */

const NAV = [
  ['Home', '#hero'], ['About', '#about'], ['Legacy', '#legacy'], ['Marble Evolution', '#evolution'],
  ['Services', '#services'], ['Projects', '#projects'], ['Gallery', '#gallery'], ['Clients', '#clients'], ['Contact', '#contact'],
];

const LEGACY_ERAS = [
  { year: '1974', title: 'Traditional Craftsmanship', desc: 'Founded on hand tools, artisan skill and an uncompromising eye for natural stone.' },
  { year: '1980s–90s', title: 'Marble & Flooring Expertise', desc: 'Deep specialization in marble and flooring execution across landmark residences and institutions.' },
  { year: '2000s', title: 'Machinery & Precision', desc: 'Mechanized cutting, grinding and polishing raise accuracy and finish to a new standard.' },
  { year: '2010s', title: 'Large-Scale Project Execution', desc: 'Professional site teams deliver high-rise, commercial and hospitality projects at scale.' },
  { year: '2020s', title: 'Digital Planning & Technology', desc: '3D visualization, digital measurement and precision planning enter every project.' },
  { year: '2026', title: 'Next-Generation Execution', desc: 'Fifty years of craft, fused with technology — built for the next fifty.' },
];

const EVOLUTION_ERAS = [
  {
    era: '1970s — 1980s', title: 'Traditional Craftsmanship', tag: 'HANDCRAFTED MARBLE',
    image: IMG.slab1,
    points: ['Hand tools & artisan skill', 'Manual measurement', 'Hand cutting', 'Traditional polishing', 'Artisan installation'],
  },
  {
    era: '1990s', title: 'Mechanical Evolution', tag: 'MACHINERY',
    image: IMG.slab2,
    points: ['Cutting machines', 'Grinding machines', 'Mechanical polishing', 'Improved measurement'],
  },
  {
    era: '2000s', title: 'Precision & Scale', tag: 'PRECISION',
    image: IMG.hero,
    points: ['Advanced cutting', 'Large-format marble', 'Precision measurement', 'Alignment systems', 'Improved finishing'],
  },
  {
    era: '2010s', title: 'Modern Project Execution', tag: 'LARGE-SCALE PROJECTS',
    image: IMG.pentLiv,
    points: ['Professional site teams', 'Large projects', 'Modern tools', 'Inspection protocols', 'Project coordination'],
  },
  {
    era: '2020s — 2026', title: 'Digital Execution', tag: 'DIGITAL TECHNOLOGY',
    image: IMG.chandelier,
    points: ['Digital drawings', '3D visualization', 'BIM coordination', 'Digital measurement', 'Precision planning', 'Modern project management'],
  },
];

const EXPERTISE = [
  { title: 'Marble Work', desc: 'Luxury marble flooring, wall cladding and precision installation.', img: IMG.slab1, icon: Gem },
  { title: 'Granite', desc: 'Heavy-duty granite flooring, counters and facade-grade execution.', img: IMG.slab2, icon: Layers },
  { title: 'Tiles', desc: 'Large-format tile laying with perfect alignment and leveling.', img: IMG.window, icon: Boxes },
  { title: 'Stone Cladding', desc: 'Exterior and interior natural stone cladding systems.', img: IMG.about, icon: Building2 },
  { title: 'Wooden Flooring', desc: 'Engineered and solid wood flooring with refined finishing.', img: IMG.pentBed, icon: Hammer },
  { title: 'Vinyl Flooring', desc: 'Commercial-grade vinyl and resilient flooring solutions.', img: IMG.pentLiv, icon: Layers },
  { title: 'Industrial Flooring', desc: 'High-performance flooring for industrial and utility spaces.', img: IMG.chandelier, icon: Workflow },
  { title: 'Turnkey Interior Execution', desc: 'Complete interior execution — from planning to final handover.', img: IMG.hero, icon: Compass },
];

const PROJECT_CATEGORIES = ['all', 'marble', 'flooring', 'interiors', 'stone', 'commercial', 'residential'];

const PROJECTS = [
  { id: 1, cat: 'marble', title: 'Bookmatched Marble Lobby', scope: 'Large-format marble flooring & feature walls', img: IMG.hero },
  { id: 2, cat: 'commercial', title: 'High-Rise Entrance Execution', scope: 'Commercial lobby stone & flooring package', img: IMG.about },
  { id: 3, cat: 'interiors', title: 'Turnkey Interior Fit-Out', scope: 'End-to-end interior execution & finishing', img: IMG.chandelier },
  { id: 4, cat: 'stone', title: 'Natural Stone Cladding', scope: 'Exterior stone cladding & facade detailing', img: IMG.window },
  { id: 5, cat: 'flooring', title: 'Precision Flooring Program', scope: 'Diamond-polished flooring across large floor plates', img: IMG.slab1 },
  { id: 6, cat: 'residential', title: 'Residential Marble Suites', scope: 'Marble, wooden & vinyl flooring for residences', img: IMG.pentBed },
  { id: 7, cat: 'marble', title: 'Italian Marble Installation', scope: 'Premium slab laying with bookmatch alignment', img: IMG.slab2 },
  { id: 8, cat: 'residential', title: 'Penthouse Living Spaces', scope: 'Full flooring & interior scope for penthouses', img: IMG.pentLiv },
];

const GALLERY = [
  { id: 1, cat: 'marble', caption: 'Italian Marble — Slab Selection', img: IMG.slab1 },
  { id: 2, cat: 'interiors', caption: 'Grand Living Hall Execution', img: IMG.hero },
  { id: 3, cat: 'commercial', caption: 'Corporate Lobby Flooring', img: IMG.about },
  { id: 4, cat: 'interiors', caption: 'Chandelier Hall — Finished Ceiling & Floor', img: IMG.chandelier },
  { id: 5, cat: 'stone', caption: 'Stone-Clad Architectural Facade', img: IMG.window },
  { id: 6, cat: 'marble', caption: 'Dark Marble — Vein Matching', img: IMG.slab2 },
  { id: 7, cat: 'residential', caption: 'Penthouse Bedroom Flooring', img: IMG.pentBed },
  { id: 8, cat: 'flooring', caption: 'Large-Format Floor Installation', img: IMG.pentLiv },
];

const CLIENTS = ['GODREJ', 'INDIABULLS', 'DB REALTY', 'RAYMOND', 'BALWA GROUP', 'MAHAVIR GROUP', 'RAHEJA GROUP'];

const WHY = [
  { icon: Award, title: '50+ Years Experience', desc: 'Five decades of continuous execution since 1974.' },
  { icon: Gem, title: 'Marble & Flooring Expertise', desc: 'Deep, specialized mastery of stone and flooring systems.' },
  { icon: Building2, title: 'Large-Scale Execution', desc: 'Proven capability across high-rise and commercial projects.' },
  { icon: Users, title: 'Skilled Workforce', desc: 'Trained in-house teams of masons, layers and finishers.' },
  { icon: ShieldCheck, title: 'Quality-Driven Approach', desc: 'Rigorous inspection at every stage of execution.' },
  { icon: Compass, title: 'Trusted by Leading Developers', desc: 'Repeat partnerships with India\'s premier developers.' },
];

const PROCESS = [
  ['01', 'Planning'], ['02', 'Measurement & Layout'], ['03', 'Material Selection'], ['04', 'Cutting & Preparation'],
  ['05', 'Installation'], ['06', 'Polishing & Finishing'], ['07', 'Quality Inspection'], ['08', 'Final Handover'],
];

/* ============================== ATOMS ============================== */

const SectionHead = ({ kicker, title, center = true, light = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.7 }}
    className={`${center ? 'text-center mx-auto' : ''} max-w-3xl mb-16`}
  >
    <span className="inline-flex items-center gap-3 text-[11px] font-mono tracking-[0.35em] text-[#D4AF37] uppercase" data-testid="section-kicker">
      <span className="w-8 h-px bg-[#D4AF37]/70" />{kicker}<span className="w-8 h-px bg-[#D4AF37]/70" />
    </span>
    <h2 className={`mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.08] ${light ? 'text-[#0A0A0A]' : 'text-[#F5F3EE]'}`}>
      {title}
    </h2>
  </motion.div>
);

const Counter = ({ to, suffix = '', duration = 2.2 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to, duration]);
  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
};

/* ============================== APP ============================== */

export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [slabFinish, setSlabFinish] = useState('white');

  const SLAB_FINISHES = [
    { id: 'white', label: 'White Galaxy', tex: '/marble-white-galaxy.jpg' },
    { id: 'black', label: 'Black Marquina', tex: '/marble-black-marquina.jpg' },
    { id: 'green', label: 'Green Marble', tex: '/marble-green.jpg' },
  ];
  const activeFinish = SLAB_FINISHES.find(f => f.id === slabFinish);
  const [eraIdx, setEraIdx] = useState(0);
  const [projectCat, setProjectCat] = useState('all');
  const [galleryCat, setGalleryCat] = useState('all');
  const [lightbox, setLightbox] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', service: 'Marble Work', message: '' });
  const [formState, setFormState] = useState('idle'); // idle | sending | done

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroImgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const heroImgScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const heroContentY = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const heroFade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lenis smooth momentum scrolling
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const raf = (time) => { lenis.raf(time); rafId = requestAnimationFrame(raf); };
    rafId = requestAnimationFrame(raf);
    const onAnchorClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const el = document.querySelector(a.getAttribute('href'));
      if (el) { e.preventDefault(); lenis.scrollTo(el, { offset: -72 }); }
    };
    document.addEventListener('click', onAnchorClick);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); document.removeEventListener('click', onAnchorClick); };
  }, []);

  // Auto-advance Marble Evolution eras
  useEffect(() => {
    const t = setInterval(() => setEraIdx((i) => (i + 1) % EVOLUTION_ERAS.length), 5200);
    return () => clearInterval(t);
  }, []);

  const selectEra = useCallback((i) => setEraIdx(i), []);

  const filteredProjects = projectCat === 'all' ? PROJECTS : PROJECTS.filter(p => p.cat === projectCat);
  const filteredGallery = galleryCat === 'all' ? GALLERY : GALLERY.filter(g => g.cat === galleryCat);

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please provide your name and a valid email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setFormState('sending');

    // Primary delivery: WhatsApp to +91 90045 68134 (works on any static hosting)
    const waText =
      `New Project Enquiry — Nilkanth Interiorrs\n\n` +
      `Name: ${form.name}\n` +
      `Email: ${form.email}\n` +
      `Phone: ${form.phone || '—'}\n` +
      `Service: ${form.service}\n` +
      `Details: ${form.message || '—'}`;
    window.open(`https://wa.me/919004568134?text=${encodeURIComponent(waText)}`, '_blank');

    // Best-effort backup save to database (ignored if backend is unreachable)
    axios.post(`${API}/enquiries`, {
      name: form.name,
      email: form.email,
      phone: form.phone,
      company: '',
      message: `[Service: ${form.service}] ${form.message}`,
    }).catch(() => {});

    setFormState('done');
    toast.success('Enquiry ready in WhatsApp — just press Send.');
  };

  const era = EVOLUTION_ERAS[eraIdx];

  return (
    <div className="min-h-screen bg-[#060606] text-[#F5F3EE] grain" data-testid="app-root">
      <Toaster theme="dark" position="top-center" richColors />

      {/* ================= HEADER ================= */}
      <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? 'glass-luxury border-b border-[#D4AF37]/15' : 'bg-gradient-to-b from-black/70 to-transparent'}`}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <a href="#hero" className="flex items-center gap-3 group" data-testid="nav-logo">
            <div className="w-11 h-11 border border-[#D4AF37] flex items-center justify-center relative">
              <span className="font-serif text-lg font-bold gold-gradient-text">N</span>
              <span className="absolute inset-[3px] border border-[#D4AF37]/30 pointer-events-none" />
            </div>
            <div className="leading-none">
              <span className="font-serif text-lg tracking-[0.18em] font-semibold gold-gradient-text block">NILKANTH</span>
              <span className="text-[9px] tracking-[0.4em] text-[#A1A1AA] uppercase font-mono block mt-1">Interiorrs • Est. 1974</span>
            </div>
          </a>

          <nav className="hidden xl:flex items-center gap-7 text-[11px] font-medium tracking-[0.2em] uppercase">
            {NAV.map(([label, href]) => (
              <a key={label} href={href} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-[#B9B5AC] hover:text-[#D4AF37] transition-colors duration-300 relative after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-[#D4AF37] after:transition-all after:duration-300 hover:after:w-full">
                {label}
              </a>
            ))}
          </nav>

          <a href="#contact" data-testid="nav-enquire-btn"
            className="hidden xl:inline-flex items-center gap-2 px-7 py-3 border border-[#D4AF37]/60 text-[#D4AF37] text-[11px] font-semibold tracking-[0.25em] uppercase shimmer-sweep hover:bg-[#D4AF37] hover:text-black transition-colors duration-400">
            Enquire
          </a>

          <button onClick={() => setMenuOpen(!menuOpen)} className="xl:hidden text-[#D4AF37] p-2" data-testid="mobile-menu-toggle" aria-label="Menu">
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-[#060606]/97 backdrop-blur-xl pt-28 px-8 xl:hidden" data-testid="mobile-menu">
            <div className="flex flex-col">
              {NAV.map(([label, href], i) => (
                <motion.a key={label} href={href} onClick={() => setMenuOpen(false)}
                  initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                  className="font-serif text-2xl py-4 border-b border-white/8 text-[#F5F3EE] hover:text-[#D4AF37] transition-colors flex items-center justify-between"
                  data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}>
                  {label}<ChevronRight className="w-4 h-4 text-[#D4AF37]" />
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ================= 1. CINEMATIC HERO ================= */}
      <section id="hero" ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y: heroImgY, scale: heroImgScale }} className="absolute inset-0">
          <img src="/palace-hero.jpg" alt="Grand Indian palace interior — Sheesh Mahal" className="w-full h-full object-cover kenburns" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#060606]/85 via-[#060606]/45 to-[#060606]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(6,6,6,0.55)_100%)]" />
          <div className="absolute inset-0 blueprint-grid opacity-40" />
        </motion.div>

        {/* Gold corner ornaments */}
        <div className="absolute top-24 left-6 lg:left-10 w-16 h-16 border-l border-t border-[#D4AF37]/40 pointer-events-none" />
        <div className="absolute bottom-10 right-6 lg:right-10 w-16 h-16 border-r border-b border-[#D4AF37]/40 pointer-events-none" />

        {/* Vertical side text */}
        <div className="hidden lg:flex absolute left-10 top-1/2 -translate-y-1/2 items-center gap-4 [writing-mode:vertical-rl] text-[10px] font-mono tracking-[0.5em] text-[#D4AF37]/60 uppercase">
          Craftsmanship that evolved with time
        </div>

        <motion.div style={{ opacity: heroFade, y: heroContentY }} className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 w-full pt-32 pb-24 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.15 }}>
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className="w-14 h-px bg-[#D4AF37]" />
              <span className="text-[11px] font-mono tracking-[0.45em] text-[#D4AF37] uppercase">Established 1974</span>
              <span className="w-14 h-px bg-[#D4AF37]" />
            </div>

            <h1 className="font-serif font-semibold leading-[0.98] tracking-tight text-[13vw] sm:text-7xl lg:text-[6.2rem]" data-testid="hero-title">
              <span className="block overflow-hidden pb-1"><motion.span className="block" initial={{ y: '115%' }} animate={{ y: 0 }} transition={{ duration: 1.15, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}>NILKANTH</motion.span></span>
              <span className="block overflow-hidden pb-1"><motion.span className="block gold-gradient-text italic" initial={{ y: '115%' }} animate={{ y: 0 }} transition={{ duration: 1.15, delay: 0.42, ease: [0.22, 1, 0.36, 1] }}>INTERIORRS</motion.span></span>
            </h1>

            <p className="mt-7 text-xl sm:text-2xl font-cormorant italic text-[#E8E4DA] tracking-[0.25em]" data-testid="hero-tagline">
              Beyond Spaces<span className="text-[#D4AF37]">.</span>
            </p>
            <p className="mt-3 text-[10px] font-mono tracking-[0.35em] text-[#A1A1AA] uppercase">
              50+ Years of Excellence — Craftsmanship • Evolution • Excellence
            </p>

            <div className="flex flex-wrap justify-center gap-5 mt-12">
              <a href="#legacy" data-testid="hero-explore-legacy-btn"
                className="group inline-flex items-center gap-4 px-9 py-4 bg-gradient-to-r from-[#E9CE7A] via-[#D4AF37] to-[#B8912A] text-black text-[11px] font-bold tracking-[0.3em] uppercase shadow-[0_10px_40px_rgba(212,175,55,0.35)] hover:shadow-[0_10px_55px_rgba(212,175,55,0.55)] transition-shadow duration-500">
                Explore Our Legacy
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a href="#projects" data-testid="hero-view-projects-btn"
                className="group inline-flex items-center gap-4 px-9 py-4 border border-white/25 text-[#F5F3EE] text-[11px] font-semibold tracking-[0.3em] uppercase hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-400 backdrop-blur-sm">
                View Projects
                <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-400" />
              </a>
            </div>
          </motion.div>

          {/* Animated legacy timeline strip */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 1 }} className="mt-20 max-w-3xl mx-auto" data-testid="hero-timeline">
            <div className="flex justify-between text-[10px] font-mono tracking-[0.3em] text-[#A1A1AA] mb-3">
              <span className="text-[#D4AF37]">1974</span><span>1990</span><span>2005</span><span>2020</span><span className="text-[#D4AF37]">2026</span>
            </div>
            <div className="relative h-px bg-white/15">
              <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37] via-[#F3E5AB] to-[#D4AF37] timeline-draw" />
              <span className="absolute -top-[3px] right-0 w-[7px] h-[7px] rounded-full bg-[#F3E5AB] pulse-dot" />
            </div>
            <p className="mt-4 text-[10px] font-mono tracking-[0.35em] text-[#A1A1AA] uppercase">1974 → 2026 — Five Decades of Continuous Evolution</p>
          </motion.div>
        </motion.div>

        {/* Interactive 3D Marble Slab — floating widget */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5 }}
          className="hidden lg:block absolute bottom-20 right-10 w-[340px] h-[240px] z-10"
          data-testid="hero-3d-slab"
        >
          <MarbleSlab3D texture={activeFinish.tex} />
          {/* Finish switcher */}
          <div className="absolute -top-9 left-0 flex gap-1.5 z-10">
            {SLAB_FINISHES.map((f) => (
              <button
                key={f.id}
                onClick={() => setSlabFinish(f.id)}
                className={`px-3 py-1.5 text-[8px] font-mono tracking-[0.25em] uppercase border backdrop-blur-md transition-all duration-300 ${
                  slabFinish === f.id
                    ? 'bg-[#D4AF37] text-black border-[#D4AF37] font-bold'
                    : 'bg-black/50 text-[#A1A1AA] border-white/20 hover:border-[#D4AF37]/60 hover:text-[#F3E5AB]'
                }`}
                data-testid={`slab-finish-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 whitespace-nowrap text-[8px] font-mono tracking-[0.4em] uppercase text-[#D4AF37]/80 pointer-events-none">
            Drag to Rotate • Live 3D Marble Slab
          </div>
          <div className="absolute -top-9 right-0 px-3 py-1.5 bg-black/50 backdrop-blur-md border border-[#D4AF37]/40 pointer-events-none">
            <span className="text-[8px] font-mono tracking-[0.3em] text-[#D4AF37] uppercase">Artech Heritages • {activeFinish.label}</span>
          </div>
        </motion.div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#A1A1AA] z-10">
          <span className="text-[9px] font-mono tracking-[0.4em] uppercase">Scroll</span>
          <ArrowDown className="w-4 h-4 text-[#D4AF37] animate-bounce" />
        </div>
      </section>

      {/* ================= EDITORIAL MARQUEE ================= */}
      <div className="relative py-9 border-y border-[#D4AF37]/15 bg-[#080807] overflow-hidden select-none" data-testid="editorial-marquee">
        <div className="marquee-track items-center" style={{ animationDuration: '58s' }}>
          {[...Array(2)].map((_, d) => (
            <div key={d} className="flex items-center gap-16 pr-16 shrink-0">
              {['MARBLE', 'FLOORING', 'STONE', 'INTERIOR EXECUTION', 'ESTABLISHED 1974', 'LARGE-SCALE PROJECTS'].map((w, i) => (
                <span key={i} className="flex items-center gap-16 shrink-0">
                  <span className={`font-serif text-3xl lg:text-5xl tracking-wide whitespace-nowrap ${i % 2 === 0 ? 'text-[#33312b]' : 'ghost-text'}`}>{w}</span>
                  <span className="w-2 h-2 rotate-45 bg-[#D4AF37]/60 shrink-0" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ================= 2. ABOUT ================= */}
      <section id="about" className="relative py-28 lg:py-40 bg-[#0A0A0A]" data-testid="about-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: '-100px' }} transition={{ duration: 0.9 }} className="relative">
            <div className="gold-frame relative overflow-hidden">
              <img src={IMG.about} alt="Premium architectural interior by Nilkanth" className="w-full h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
            <div className="absolute -bottom-8 -right-4 lg:-right-8 glass-luxury px-8 py-6 floaty" data-testid="about-badge">
              <span className="block text-5xl font-serif font-bold gold-gradient-text leading-none">50+</span>
              <span className="block mt-2 text-[10px] font-mono tracking-[0.3em] text-[#A1A1AA] uppercase">Years of Experience</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-100px' }} transition={{ duration: 0.9 }}>
            <span className="inline-flex items-center gap-3 text-[11px] font-mono tracking-[0.35em] text-[#D4AF37] uppercase">
              <span className="w-8 h-px bg-[#D4AF37]/70" />About Nilkanth
            </span>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.1]">
              A Legacy Built in <span className="gold-gradient-text italic">Stone & Marble</span>
            </h2>
            <p className="mt-7 text-[#B9B5AC] text-base leading-relaxed font-light">
              Established in 1974, Nilkanth Interiorrs specializes in marble, flooring, stone and complete
              interior execution — delivering large-scale projects with a skilled execution team, quality-focused
              workmanship, modern tools and professional project management.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 mt-9">
              {['Established in 1974', '50+ years of experience', 'Marble & flooring expertise', 'Skilled execution team', 'Quality-focused workmanship', 'Large-scale project capability', 'Modern tools & technology', 'Professional project execution'].map((item) => (
                <div key={item} className="flex items-center gap-3 text-sm text-[#E8E4DA]">
                  <span className="w-1.5 h-1.5 rotate-45 bg-[#D4AF37] shrink-0" />{item}
                </div>
              ))}
            </div>
            <div className="hairline-gold mt-10" />
            <p className="mt-6 font-cormorant italic text-xl text-[#D4AF37]/90">"Craftsmanship that evolved with time."</p>
          </motion.div>
        </div>
      </section>

      {/* ================= 3. LEGACY TIMELINE ================= */}
      <section id="legacy" className="relative py-28 lg:py-40 bg-[#060606] overflow-hidden" data-testid="legacy-section">
        <span className="absolute top-10 left-0 font-serif font-bold text-[22vw] leading-none ghost-text-white select-none pointer-events-none whitespace-nowrap">1974</span>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <SectionHead kicker="Legacy 1974 — 2026" title={<>50+ Years of <span className="gold-gradient-text italic">Evolution</span></>} />
          <p className="text-center font-cormorant italic text-xl sm:text-2xl text-[#C9C5BB] max-w-2xl mx-auto -mt-8 mb-20">
            "We didn't just survive 50 years — <span className="text-[#D4AF37]">we evolved with the industry.</span>"
          </p>

          <div className="relative">
            <div className="absolute left-4 lg:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#D4AF37]/60 via-white/10 to-[#D4AF37]/60" />
            {LEGACY_ERAS.map((m, i) => (
              <motion.div key={m.year}
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, delay: 0.05 }}
                className={`relative flex items-start gap-8 mb-14 last:mb-0 pl-14 lg:pl-0 ${i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
                data-testid={`legacy-era-${i}`}>
                <span className="absolute left-4 lg:left-1/2 top-2 -translate-x-1/2 w-3 h-3 rotate-45 bg-[#060606] border border-[#D4AF37]" />
                <div className={`lg:w-1/2 ${i % 2 === 0 ? 'lg:pr-16 lg:text-right' : 'lg:pl-16'}`}>
                  <span className="font-serif text-4xl sm:text-5xl font-bold gold-gradient-text">{m.year}</span>
                  <h3 className="mt-2 text-lg font-semibold tracking-wide text-[#F5F3EE] uppercase">{m.title}</h3>
                  <p className={`mt-3 text-sm text-[#A1A1AA] leading-relaxed font-light ${i % 2 === 0 ? 'lg:ml-auto' : ''} max-w-md`}>{m.desc}</p>
                </div>
                <div className="hidden lg:block lg:w-1/2" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= 4. MARBLE EVOLUTION (MAIN FEATURE) ================= */}
      <section id="evolution" className="relative py-28 lg:py-40 bg-[#0C0C0C] border-y border-[#D4AF37]/10 overflow-hidden" data-testid="evolution-section">
        <div className="absolute inset-0 blueprint-grid opacity-40 pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <SectionHead kicker="The Main Feature" title={<>Marble Evolution — <span className="gold-gradient-text italic">From Craftsmanship to Precision</span></>} />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">
            {/* Era selector */}
            <div className="lg:col-span-4 flex flex-col gap-3">
              {EVOLUTION_ERAS.map((e, i) => (
                <button key={e.era} onClick={() => selectEra(i)} data-testid={`evo-era-btn-${i}`}
                  className={`group text-left p-5 border transition-all duration-500 relative overflow-hidden ${
                    eraIdx === i ? 'border-[#D4AF37] bg-[#D4AF37]/8' : 'border-white/10 bg-white/[0.02] hover:border-[#D4AF37]/40'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className={`text-[10px] font-mono tracking-[0.3em] uppercase ${eraIdx === i ? 'text-[#D4AF37]' : 'text-[#A1A1AA]'}`}>{e.era}</span>
                      <h3 className={`font-serif text-lg font-semibold mt-1 ${eraIdx === i ? 'text-[#F3E5AB]' : 'text-[#C9C5BB]'}`}>{e.title}</h3>
                    </div>
                    <span className={`font-serif text-3xl font-bold ${eraIdx === i ? 'gold-gradient-text' : 'ghost-text-white'}`}>0{i + 1}</span>
                  </div>
                  {eraIdx === i && (
                    <motion.div key={`bar-${i}`} initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 5.2, ease: 'linear' }}
                      className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#D4AF37] to-[#F3E5AB] origin-left" />
                  )}
                </button>
              ))}
            </div>

            {/* Era visual */}
            <div className="lg:col-span-8 relative min-h-[480px] border border-white/10 overflow-hidden gold-frame" data-testid="evo-visual">
              <AnimatePresence mode="wait">
                <motion.div key={eraIdx} initial={{ opacity: 0, scale: 1.06 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-0">
                  <img src={era.image} alt={era.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#060606] via-[#060606]/40 to-transparent" />
                </motion.div>
              </AnimatePresence>

              <div className="absolute bottom-0 inset-x-0 p-8 lg:p-12 z-10">
                <span className="text-[10px] font-mono tracking-[0.4em] text-[#D4AF37] uppercase">{era.tag}</span>
                <h3 className="font-serif text-3xl lg:text-5xl font-bold mt-2">{era.title}</h3>
                <div className="flex flex-wrap gap-2.5 mt-5">
                  {era.points.map((p) => (
                    <span key={p} className="px-4 py-1.5 text-[11px] tracking-wider uppercase glass-luxury text-[#E8E4DA]">{p}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Transformation strip */}
          <div className="mt-16 flex flex-wrap items-center justify-center gap-x-4 gap-y-3 text-[11px] font-mono tracking-[0.25em] uppercase text-[#A1A1AA]" data-testid="evo-transformation">
            {['Handcrafted Marble', 'Machinery', 'Precision', 'Large-Scale Projects', 'Digital Technology'].map((s, i) => (
              <React.Fragment key={s}>
                <span className={i === eraIdx ? 'text-[#D4AF37]' : ''}>{s}</span>
                {i < 4 && <ChevronRight className="w-3.5 h-3.5 text-[#D4AF37]/60" />}
              </React.Fragment>
            ))}
          </div>
          <div className="text-center mt-12">
            <p className="font-serif text-2xl sm:text-3xl font-semibold gold-gradient-text">Next-Generation Marble Execution</p>
            <p className="mt-3 text-[11px] font-mono tracking-[0.35em] text-[#A1A1AA] uppercase">50 Years of Evolution — One Standard of Excellence</p>
          </div>
        </div>
      </section>

      {/* ================= 5. EXPERTISE ================= */}
      <section id="services" className="py-28 lg:py-40 bg-[#060606]" data-testid="expertise-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionHead kicker="Our Expertise" title={<>Mastery Across <span className="gold-gradient-text italic">Every Surface</span></>} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {EXPERTISE.map((s, i) => (
              <motion.div key={s.title}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6, delay: (i % 4) * 0.08 }}
                className="tilt-card group relative h-80 overflow-hidden border border-white/10 cursor-pointer"
                data-testid={`expertise-card-${i}`}>
                <img src={s.img} alt={s.title} className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-110 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#060606] via-[#060606]/55 to-[#060606]/20" />
                <div className="relative h-full flex flex-col justify-between p-6">
                  <s.icon className="w-7 h-7 text-[#D4AF37]" strokeWidth={1.25} />
                  <div>
                    <span className="text-[9px] font-mono tracking-[0.35em] text-[#D4AF37]/80 uppercase">0{i + 1}</span>
                    <h3 className="font-serif text-xl font-semibold mt-1 leading-snug">{s.title}</h3>
                    <p className="text-xs text-[#A1A1AA] mt-2 leading-relaxed opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">{s.desc}</p>
                  </div>
                </div>
                <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-gradient-to-r from-[#D4AF37] to-[#F3E5AB] group-hover:w-full transition-all duration-700" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= 6. PROJECT EXPERIENCE ================= */}
      <section id="projects" className="py-28 lg:py-40 bg-[#0A0A0A] border-t border-white/5" data-testid="projects-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
            <SectionHead center={false} kicker="Project Experience" title={<>Executed at <span className="gold-gradient-text italic">Scale</span></>} />
            <div className="flex flex-wrap gap-2" data-testid="project-filters">
              {PROJECT_CATEGORIES.map((c) => (
                <button key={c} onClick={() => setProjectCat(c)} data-testid={`project-filter-${c}`}
                  className={`px-5 py-2 text-[10px] font-mono tracking-[0.25em] uppercase border transition-all duration-300 ${
                    projectCat === c ? 'border-[#D4AF37] bg-[#D4AF37] text-black font-bold' : 'border-white/15 text-[#A1A1AA] hover:border-[#D4AF37]/50 hover:text-[#F3E5AB]'}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredProjects.map((p) => (
              <motion.div key={p.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.45 }}
                className="group relative h-[420px] overflow-hidden border border-white/10 cursor-pointer"
                onClick={() => setLightbox(p)} data-testid={`project-card-${p.id}`}>
                <img src={p.img} alt={p.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s] ease-out" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#060606] via-[#060606]/25 to-transparent" />
                <span className="absolute top-5 left-5 px-3 py-1 text-[9px] font-mono tracking-[0.3em] uppercase glass-luxury text-[#D4AF37]">{p.cat}</span>
                <button className="absolute top-5 right-5 w-10 h-10 glass-luxury flex items-center justify-center text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                  data-testid={`project-zoom-${p.id}`} aria-label="View fullscreen">
                  <Eye className="w-4 h-4" />
                </button>
                <div className="absolute bottom-0 inset-x-0 p-7">
                  <h3 className="font-serif text-2xl font-semibold">{p.title}</h3>
                  <p className="text-xs text-[#A1A1AA] mt-2 tracking-wide uppercase font-mono">{p.scope}</p>
                  <span className="block mt-4 h-px w-0 group-hover:w-full bg-gradient-to-r from-[#D4AF37] to-transparent transition-all duration-700" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= 7. PICTURE GALLERY ================= */}
      <section id="gallery" className="py-28 lg:py-40 bg-[#060606]" data-testid="gallery-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionHead kicker="Picture Gallery" title={<>An Architectural <span className="gold-gradient-text italic">Portfolio</span></>} />
          <div className="flex flex-wrap justify-center gap-2 mb-12" data-testid="gallery-filters">
            {PROJECT_CATEGORIES.map((c) => (
              <button key={c} onClick={() => setGalleryCat(c)} data-testid={`gallery-filter-${c}`}
                className={`px-5 py-2 text-[10px] font-mono tracking-[0.25em] uppercase border transition-all duration-300 ${
                  galleryCat === c ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-white/10 text-[#A1A1AA] hover:border-[#D4AF37]/40'}`}>
                {c}
              </button>
            ))}
          </div>

          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:balance]">
            {filteredGallery.map((g, i) => (
              <motion.figure key={g.id}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                className="relative mb-5 break-inside-avoid overflow-hidden group cursor-pointer border border-white/10"
                onClick={() => setLightbox({ ...g, title: g.caption, scope: g.cat })}
                data-testid={`gallery-item-${g.id}`}>
                <img src={g.img} alt={g.caption} className={`w-full object-cover group-hover:scale-105 transition-transform duration-[1.1s] ${i % 3 === 1 ? 'h-72' : 'h-96'}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <figcaption className="absolute bottom-0 inset-x-0 p-5 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  <span className="text-[9px] font-mono tracking-[0.3em] text-[#D4AF37] uppercase">{g.cat}</span>
                  <p className="font-serif text-base mt-1">{g.caption}</p>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* ================= 8. SCALE & ACHIEVEMENTS ================= */}
      <section className="relative py-28 lg:py-36 bg-[#0C0C0C] border-y border-[#D4AF37]/10 overflow-hidden" data-testid="achievements-section">
        <span className="absolute -top-6 right-0 font-serif font-bold text-[20vw] leading-none ghost-text select-none pointer-events-none">50+</span>
        <div className="absolute inset-0 blueprint-grid opacity-30 pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <SectionHead kicker="Scale & Achievements" title={<>Numbers That <span className="gold-gradient-text italic">Command Trust</span></>} />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {[
              { val: 50, suffix: '+', label: 'Years of Experience' },
              { val: 1200, suffix: 'M+', label: 'Sq. Ft. Completed' },
              { val: 1974, suffix: '', label: 'Year Established', raw: true },
              { val: null, label: 'Large-Scale Project Execution' },
            ].map((s, i) => (
              <div key={i} className="bg-[#0C0C0C] p-10 lg:p-14 text-center group hover:bg-[#111111] transition-colors duration-500" data-testid={`stat-card-${i}`}>
                {s.val === null ? (
                  <span className="font-serif text-3xl lg:text-4xl font-bold gold-gradient-text leading-none">LARGE-<br />SCALE</span>
                ) : s.raw ? (
                  <span className="font-serif text-5xl lg:text-7xl font-bold gold-gradient-text leading-none">{s.val}</span>
                ) : (
                  <span className="font-serif text-5xl lg:text-7xl font-bold gold-gradient-text leading-none"><Counter to={s.val} suffix={s.suffix} /></span>
                )}
                <p className="mt-5 text-[10px] font-mono tracking-[0.3em] text-[#A1A1AA] uppercase">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= 9. TRUSTED CLIENTS ================= */}
      <section id="clients" className="py-24 lg:py-32 bg-[#060606] overflow-hidden" data-testid="clients-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionHead kicker="Trusted Clients" title={<>Trusted by <span className="gold-gradient-text italic">Leading Developers</span></>} />
        </div>
        <div className="relative border-y border-white/10 py-12" data-testid="clients-marquee">
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#060606] to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#060606] to-transparent z-10 pointer-events-none" />
          <div className="marquee-track gap-24 px-12">
            {[...CLIENTS, ...CLIENTS].map((c, i) => (
              <span key={i} className="font-serif text-3xl lg:text-4xl font-semibold tracking-[0.15em] text-white/30 hover:text-[#D4AF37] hover:drop-shadow-[0_0_18px_rgba(212,175,55,0.4)] transition-all duration-500 whitespace-nowrap cursor-default"
                data-testid={i < CLIENTS.length ? `client-${c.toLowerCase().replace(/\s+/g, '-')}` : undefined}>
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ================= 10. WHY NILKANTH ================= */}
      <section className="py-28 lg:py-40 bg-[#0A0A0A]" data-testid="why-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionHead kicker="Why Nilkanth" title={<>The Nilkanth <span className="gold-gradient-text italic">Standard</span></>} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {WHY.map((w, i) => (
              <motion.div key={w.title}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
                className="bg-[#0A0A0A] p-10 group hover:bg-[#101010] transition-colors duration-500 relative overflow-hidden"
                data-testid={`why-card-${i}`}>
                <span className="absolute -bottom-6 -right-2 font-serif text-8xl font-bold ghost-text-white group-hover:ghost-text transition-all duration-500 select-none">0{i + 1}</span>
                <w.icon className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.1} />
                <h3 className="font-serif text-xl font-semibold mt-6 leading-snug">{w.title}</h3>
                <p className="text-sm text-[#A1A1AA] mt-3 font-light leading-relaxed">{w.desc}</p>
                <span className="block mt-6 h-px w-10 bg-[#D4AF37]/50 group-hover:w-full transition-all duration-700" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= 11. EXECUTION PROCESS ================= */}
      <section id="process" className="py-28 lg:py-40 bg-[#060606] relative overflow-hidden" data-testid="process-section">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{ backgroundImage: `url(${IMG.slab1})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <SectionHead kicker="Execution Process" title={<>Eight Steps. <span className="gold-gradient-text italic">Zero Compromise.</span></>} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROCESS.map(([num, title], i) => (
              <motion.div key={num}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.55, delay: (i % 4) * 0.08 }}
                className="glass-card p-7 relative group shimmer-sweep"
                data-testid={`process-step-${i + 1}`}>
                <div className="flex items-start justify-between">
                  <span className="font-serif text-4xl font-bold gold-gradient-text">{num}</span>
                  {i < 7 && <ChevronRight className="w-4 h-4 text-[#D4AF37]/50 mt-2 hidden lg:block" />}
                </div>
                <h3 className="mt-6 text-sm font-semibold tracking-[0.18em] uppercase text-[#E8E4DA]">{title}</h3>
                <span className="block mt-5 h-px w-full bg-white/10 group-hover:bg-[#D4AF37]/50 transition-colors duration-500" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= 12. FUTURE & TECHNOLOGY ================= */}
      <section id="future" className="relative py-28 lg:py-40 bg-[#0C0C0C] border-y border-[#D4AF37]/10 overflow-hidden" data-testid="future-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-100px' }} transition={{ duration: 0.9 }}>
            <span className="inline-flex items-center gap-3 text-[11px] font-mono tracking-[0.35em] text-[#D4AF37] uppercase">
              <span className="w-8 h-px bg-[#D4AF37]/70" />Future & Technology
            </span>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.1]">
              The Next Generation of <span className="gold-gradient-text italic">Craftsmanship</span>
            </h2>
            <div className="grid grid-cols-2 gap-x-8 gap-y-5 mt-10">
              {[
                [Ruler, 'Digital Measurement'], [Boxes, '3D Visualization'], [Workflow, 'Digital Planning'],
                [Cpu, 'Advanced Execution'], [ShieldCheck, 'Quality Control'], [Gem, 'Traditional Expertise'],
              ].map(([Icon, label]) => (
                <div key={label} className="flex items-center gap-4 group">
                  <div className="w-11 h-11 border border-[#D4AF37]/40 flex items-center justify-center group-hover:bg-[#D4AF37]/10 transition-colors duration-400">
                    <Icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.25} />
                  </div>
                  <span className="text-sm tracking-wide text-[#E8E4DA]">{label}</span>
                </div>
              ))}
            </div>
            <div className="hairline-gold mt-12" />
            <p className="mt-8 font-serif text-2xl sm:text-3xl font-semibold leading-snug">
              50 Years of Experience. <br /><span className="gold-gradient-text italic">Built for the Next 50.</span>
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.94 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: '-100px' }} transition={{ duration: 1 }}
            className="relative gold-frame">
            <img src={IMG.chandelier} alt="Futuristic architectural interior" className="w-full h-[520px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#060606]/80 via-transparent to-transparent" />
            <div className="absolute inset-0 blueprint-grid opacity-50" />
            <div className="absolute bottom-8 left-8 glass-luxury px-6 py-4">
              <span className="text-[9px] font-mono tracking-[0.3em] text-[#D4AF37] uppercase block">BIM-Coordinated Execution</span>
              <span className="font-serif text-lg">Precision, Planned Digitally</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= 13. CONTACT ================= */}
      <section id="contact" className="relative py-28 lg:py-40 bg-[#060606] overflow-hidden" data-testid="contact-section">
        <div className="absolute inset-0 opacity-10 pointer-events-none"
          style={{ backgroundImage: `url(${IMG.hero})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060606] via-[#060606]/70 to-[#060606] pointer-events-none" />

        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-3 text-[11px] font-mono tracking-[0.35em] text-[#D4AF37] uppercase">
              <span className="w-8 h-px bg-[#D4AF37]/70" />Contact
            </span>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.1]">
              Begin Your <span className="gold-gradient-text italic">Project</span>
            </h2>
            <p className="mt-4 font-cormorant italic text-lg text-[#C9C5BB]">Nilkanth Interiorrs — Established 1974 · 50+ Years of Excellence</p>

            <div className="flex flex-col gap-5 mt-10">
              {[
                [Phone, 'Phone', '+91 90045 68134 / +91 98925 38559', 'tel:+919004568134'],
                [MessageCircle, 'WhatsApp', '+91 90045 68134', 'https://wa.me/919004568134'],
                [Mail, 'Email', 'admin@nkhabitat.com', 'mailto:admin@nkhabitat.com'],
                [Globe, 'Website', 'nkhabitat.com', 'https://nkhabitat.com'],
                [AtSign, 'Instagram', '@nilkanthinteriorrs', 'https://www.instagram.com/nilkanthinteriorrs'],
                [MapPin, 'Address', 'Parel, Mumbai, India', null],
              ].map(([Icon, label, value, href]) => (
                <div key={label} className="flex items-center gap-5 group" data-testid={`contact-${label.toLowerCase()}`}>
                  <div className="w-12 h-12 border border-[#D4AF37]/40 flex items-center justify-center shrink-0 group-hover:bg-[#D4AF37]/10 transition-colors duration-400">
                    <Icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.25} />
                  </div>
                  <div>
                    <span className="text-[9px] font-mono tracking-[0.3em] text-[#A1A1AA] uppercase block">{label}</span>
                    {href
                      ? <a href={href} className="text-sm text-[#F5F3EE] hover:text-[#D4AF37] transition-colors">{value}</a>
                      : <span className="text-sm text-[#F5F3EE]">{value}</span>}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 border border-white/10 overflow-hidden" data-testid="contact-map">
              <iframe title="Nilkanth Interiorrs Location" src="https://www.google.com/maps?q=Parel,Mumbai,India&output=embed"
                className="w-full h-56 grayscale invert-[0.9] contrast-[0.9]" loading="lazy" />
            </div>
          </div>

          <div className="lg:col-span-7 glass-luxury p-8 lg:p-12 gold-frame" data-testid="enquiry-panel">
            {formState === 'done' ? (
              <div className="h-full min-h-[420px] flex flex-col items-center justify-center text-center gap-5">
                <div className="w-16 h-16 border border-[#D4AF37] rotate-45 flex items-center justify-center">
                  <CheckCircle2 className="w-7 h-7 text-[#D4AF37] -rotate-45" />
                </div>
                <h3 className="font-serif text-3xl font-semibold">Enquiry Ready in WhatsApp</h3>
                <p className="text-sm text-[#A1A1AA] max-w-sm font-light">
                  Thank you, {form.name}. WhatsApp has opened with your enquiry pre-filled — just press Send and it reaches our team at +91 90045 68134 instantly.
                </p>
                <button onClick={() => { setFormState('idle'); setForm({ name: '', email: '', phone: '', service: 'Marble Work', message: '' }); }}
                  className="mt-3 px-8 py-3 border border-[#D4AF37]/60 text-[#D4AF37] text-[11px] tracking-[0.25em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors"
                  data-testid="enquiry-reset-btn">
                  Send Another Enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={submitEnquiry} className="flex flex-col gap-6" data-testid="enquiry-form">
                <h3 className="font-serif text-2xl font-semibold">Project Enquiry</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="text-[9px] font-mono tracking-[0.3em] text-[#A1A1AA] uppercase block mb-2">Full Name *</label>
                    <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                      className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] outline-none py-3 text-sm transition-colors placeholder:text-white/25"
                      placeholder="Your name" data-testid="enquiry-name" />
                  </div>
                  <div>
                    <label className="text-[9px] font-mono tracking-[0.3em] text-[#A1A1AA] uppercase block mb-2">Email *</label>
                    <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required
                      className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] outline-none py-3 text-sm transition-colors placeholder:text-white/25"
                      placeholder="you@example.com" data-testid="enquiry-email" />
                  </div>
                  <div>
                    <label className="text-[9px] font-mono tracking-[0.3em] text-[#A1A1AA] uppercase block mb-2">Phone</label>
                    <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] outline-none py-3 text-sm transition-colors placeholder:text-white/25"
                      placeholder="+91 ..." data-testid="enquiry-phone" />
                  </div>
                  <div>
                    <label className="text-[9px] font-mono tracking-[0.3em] text-[#A1A1AA] uppercase block mb-2">Service</label>
                    <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}
                      className="w-full bg-[#111111] border-b border-white/20 focus:border-[#D4AF37] outline-none py-3 text-sm transition-colors"
                      data-testid="enquiry-service">
                      {['Marble Work', 'Granite', 'Tiles', 'Stone Cladding', 'Wooden Flooring', 'Vinyl Flooring', 'Industrial Flooring', 'Turnkey Interior Execution'].map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-[9px] font-mono tracking-[0.3em] text-[#A1A1AA] uppercase block mb-2">Project Details</label>
                  <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] outline-none py-3 text-sm transition-colors resize-none placeholder:text-white/25"
                    placeholder="Tell us about your project — scope, location, timeline..." data-testid="enquiry-message" />
                </div>
                <button type="submit" disabled={formState === 'sending'}
                  className="mt-2 w-full py-4 bg-gradient-to-r from-[#E9CE7A] via-[#D4AF37] to-[#B8912A] text-black text-[11px] font-bold tracking-[0.3em] uppercase shadow-[0_10px_40px_rgba(212,175,55,0.3)] hover:shadow-[0_10px_55px_rgba(212,175,55,0.5)] transition-shadow duration-500 flex items-center justify-center gap-3 disabled:opacity-60"
                  data-testid="enquiry-submit-btn">
                  {formState === 'sending' ? 'Sending...' : 'Submit Enquiry'}<Send className="w-4 h-4" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ================= LIGHTBOX ================= */}
      <AnimatePresence>
        {lightbox && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-xl flex items-center justify-center p-6"
            onClick={() => setLightbox(null)} data-testid="lightbox">
            <motion.div initial={{ scale: 0.92 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setLightbox(null)} data-testid="lightbox-close"
                className="absolute -top-12 right-0 text-[#D4AF37] hover:text-white transition-colors flex items-center gap-2 text-[11px] font-mono tracking-[0.3em] uppercase">
                Close <X className="w-5 h-5" />
              </button>
              <div className="gold-frame">
                <img src={lightbox.img} alt={lightbox.title} className="w-full max-h-[75vh] object-cover" />
              </div>
              <div className="mt-5 flex items-center justify-between">
                <div>
                  <span className="text-[9px] font-mono tracking-[0.3em] text-[#D4AF37] uppercase">{lightbox.cat}</span>
                  <h3 className="font-serif text-2xl font-semibold mt-1">{lightbox.title}</h3>
                </div>
                {lightbox.scope && <span className="text-xs font-mono text-[#A1A1AA] uppercase tracking-wider hidden sm:block">{lightbox.scope}</span>}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ================= FOOTER ================= */}
      <footer className="bg-[#040404] border-t border-[#D4AF37]/15 pt-20 pb-10" data-testid="footer">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 pb-14">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 border border-[#D4AF37] flex items-center justify-center">
                  <span className="font-serif text-lg font-bold gold-gradient-text">N</span>
                </div>
                <div className="leading-none">
                  <span className="font-serif text-lg tracking-[0.18em] font-semibold gold-gradient-text block">NILKANTH</span>
                  <span className="text-[9px] tracking-[0.4em] text-[#A1A1AA] uppercase font-mono block mt-1">Interiorrs • Est. 1974</span>
                </div>
              </div>
              <p className="mt-6 font-cormorant italic text-xl text-[#C9C5BB] max-w-md leading-relaxed">
                "Craftsmanship that evolved with time. Excellence that stands the test of time."
              </p>
            </div>
            <div>
              <h4 className="text-[10px] font-mono tracking-[0.35em] text-[#D4AF37] uppercase mb-5">Navigate</h4>
              <ul className="flex flex-col gap-3 text-sm text-[#A1A1AA]">
                {[['About', '#about'], ['Legacy', '#legacy'], ['Marble Evolution', '#evolution'], ['Projects', '#projects'], ['Gallery', '#gallery'], ['Contact', '#contact']].map(([l, h]) => (
                  <li key={l}><a href={h} className="hover:text-[#D4AF37] transition-colors">{l}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-[10px] font-mono tracking-[0.35em] text-[#D4AF37] uppercase mb-5">Expertise</h4>
              <ul className="flex flex-col gap-3 text-sm text-[#A1A1AA]">
                {['Marble Work', 'Granite & Tiles', 'Stone Cladding', 'Wooden & Vinyl Flooring', 'Industrial Flooring', 'Turnkey Interiors'].map(l => <li key={l}>{l}</li>)}
              </ul>
            </div>
          </div>
          <div className="hairline-gold" />
          <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-[10px] font-mono tracking-[0.25em] text-[#6b6b6b] uppercase">
            <span>© 1974–2026 Nilkanth Interiorrs.</span>
            <span>Marble • Flooring • Stone • Interior Execution</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
